<script>
import { activeBrand } from '@/themes/primevue/brandState.js';
import { Link, router } from '@inertiajs/vue3';
import Avatar from 'primevue/avatar';
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import Listbox from 'primevue/listbox';
import Menu from 'primevue/menu';
import Popover from 'primevue/popover';
import Select from 'primevue/select';
import SidebarTrigger from 'primevue/sidebartrigger';
import Toolbar from 'primevue/toolbar';
import NotificationBell from '@/Components/ShopManagement/NotificationBell.vue';
import { touchPickerPolicyMixin } from '@/Components/ShopManagement/touchPickerPolicy.js';
import TimeClockControl from '@/Components/ShopManagement/TimeClockControl.vue';
import { SETTINGS_ALL_SHOPS_QUERY_VALUE } from '@/ShopManagement/settings.js';
import { ADMIN_WORKSPACE } from '@/BayHiveAdmin/workspace.js';
import ShopSearch from '@/Components/ShopManagement/Admin/ShopSearch.vue';

export default {
  mixins: [touchPickerPolicyMixin],
  inject: { adminWorkspace: { from: ADMIN_WORKSPACE, default: null } },
  components: {
    ShopSearch,
    Avatar,
    Badge,
    Button,
    Link,
    Listbox,
    Menu,
    Popover,
    Select,
    SidebarTrigger,
    Toolbar,
    NotificationBell,
    TimeClockControl,
  },

  props: {
    shortcutsEnabled: {
      type: Boolean,
      default: true,
    },
    sidebarTarget: {
      type: String,
      default: 'shop-management-sidebar',
    },
    layoutMode: {
      type: String,
      default: 'full',
    },
    showSidebarTrigger: {
      type: Boolean,
      default: false,
    },
    narrowLayout: {
      type: Boolean,
      default: false,
    },
    selectedScope: {
      type: Object,
      default: null,
    },
    scopes: {
      type: Array,
      default: () => [],
    },
    user: {
      type: Object,
      default: null,
    },
    showEstimate: {
      type: Boolean,
      default: false,
    },
    showTimeClock: {
      type: Boolean,
      default: false,
    },
    commandPaletteOpen: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['open-command-palette', 'create-estimate', 'scope-change'],

  data() {
    return {
      currentTheme: 'light',
      commandPaletteId: 'shop-management-command-palette',
      userMenuId: 'shop-management-topbar-user-menu',
      userMenuOpen: false,
      scopePickerId: 'shop-management-topbar-scope-picker',
      scopePickerOpen: false,
    };
  },

  computed: {
    isAdmin() {
      return this.adminWorkspace?.active.value === true;
    },
    activeScope() {
      return this.selectedScope || this.scopes[0] || null;
    },

    brandHref() {
      const shopId = this.$page.props.master_config?.shop_id;

      if (!shopId) {
        return '#';
      }

      return route('shop-management.dashboard', { shop_id: shopId });
    },

    productName() {
      return activeBrand.value.productName;
    },
    productInitials() {
      return this.productName.split(/\s+/).slice(0, 2).map((word) => word[0]).join('');
    },

    logoUrl() {
      const logos = activeBrand.value.logos;
      const dark = this.currentTheme === 'dark';
      return this.showBrandLabel
        ? (dark ? logos.dark || logos.light : logos.light || logos.dark)
        : (dark
          ? logos.iconDark || logos.iconLight || logos.dark || logos.light
          : logos.iconLight || logos.iconDark || logos.light || logos.dark);
    },

    logoWidth() {
      return this.showBrandLabel ? undefined : 26;
    },

    logoHeight() {
      return 32;
    },

    estimateLabel() {
      return this.isCompactMode ? 'Estimate' : 'New Estimate';
    },

    displayName() {
      return this.user?.full_name || this.user?.email || 'User';
    },

    isSearchIconOnly() {
      return this.isIconFirstMode;
    },

    initials() {
      return this.displayName
        .split(' ')
        .map((part) => part.charAt(0))
        .join('')
        .slice(0, 2)
        .toUpperCase();
    },

    userAvatarImage() {
      return this.user?.image_url || null;
    },

    isIconFirstMode() {
      return this.layoutMode === 'icon-first';
    },

    isScopeIconOnlyMode() {
      return !this.isAdmin && this.isIconFirstMode;
    },

    scopeTriggerLabel() {
      return this.activeScope?.name ? `Select shop: ${this.activeScope.name}` : 'Select shop';
    },

    isUserIconOnlyMode() {
      return this.layoutMode !== 'full';
    },

    isCompactMode() {
      return this.layoutMode === 'compact';
    },

    searchShortcutLabel() {
      if (typeof navigator === 'undefined') {
        return 'Ctrl K';
      }

      const platform = navigator.userAgentData?.platform || navigator.platform || '';
      return /mac|iphone|ipad|ipod/i.test(platform) ? '⌘K' : 'Ctrl K';
    },

    searchShortcutAria() {
      return this.searchShortcutLabel === '⌘K' ? 'Meta+K' : 'Control+K';
    },

    showBrandLabel() {
      return this.layoutMode === 'full';
    },

    showUserChevron() {
      return this.layoutMode === 'full';
    },

    showUserLabel() {
      return this.layoutMode === 'full';
    },

    topbarClass() {
      return [
        'shop-management-topbar',
        `shop-management-topbar--${this.layoutMode}`,
        { 'shop-management-topbar--admin': this.isAdmin },
        {
          'shop-management-topbar--all-controls':
            this.showSidebarTrigger && this.showTimeClock && this.showEstimate,
        },
        { 'shop-management-topbar--narrow-layout': this.narrowLayout },
      ];
    },

    userMenuItems() {
      const shopId = this.$page.props.master_config?.shop_id;

      return [
        ...(this.$page.props.canAccessBayHiveAdmin === true && !this.isAdmin
          ? [
              {
                label: 'Super Admin',
                icon: 'fa-solid fa-user-shield',
                command: () =>
                  this.navigateTo(route('bayhive-admin.index', { shop_id: shopId }), true),
              },
            ]
          : []),
        {
          label: 'Profile',
          icon: 'fa-solid fa-user',
          command: () =>
            this.navigateTo(
              shopId ? route('shop-management.profile', { shop_id: shopId }) : '#',
              true,
            ),
        },
        {
          label: 'Change Password',
          icon: 'fa-solid fa-key',
          command: () => this.navigateTo(route('user.password'), true),
        },
        ...(this.showTimeClock ? [{
          label: 'Time Clock',
          icon: 'fa-solid fa-clock',
          command: () => this.openProfileTimeClock(),
        }] : []),
        {
          label: 'Help',
          icon: 'fa-solid fa-circle-question',
          command: () => this.navigateTo('https://bayhive.com/support/'),
        },
        { separator: true },
        {
          label: 'Sign out',
          icon: 'fa-solid fa-arrow-right-from-bracket',
          command: () =>
            this.navigateTo(shopId ? route('logout', { shop_id: shopId }) : route('logout-all')),
        },
      ];
    },
  },

  watch: {
    isScopeIconOnlyMode() {
      this.$refs.scopePicker?.hide();
      this.scopePickerOpen = false;
    },
  },

  mounted() {
    this.syncTheme();
    window.addEventListener('themechange', this.handleThemeChange);
  },

  beforeUnmount() {
    window.removeEventListener('themechange', this.handleThemeChange);
  },

  methods: {
    buildShopScopeDestination(shopId) {
      if (typeof window === 'undefined') {
        return null;
      }

      const currentShopId = Number(this.$page.props.master_config?.shop_id || 0);
      const targetShopId = Number(shopId || 0);

      if (!currentShopId || !targetShopId) {
        return null;
      }

      const currentUrl = new URL(window.location.href);
      const currentShopPath = `/shop/${currentShopId}`;

      if (
        currentUrl.pathname !== currentShopPath
        && !currentUrl.pathname.startsWith(`${currentShopPath}/`)
      ) {
        return null;
      }

      currentUrl.pathname = `/shop/${targetShopId}${currentUrl.pathname.slice(currentShopPath.length)}`;
      currentUrl.searchParams.delete('scope');
      currentUrl.searchParams.delete('selected_shop');

      return currentUrl.toString();
    },

    buildMsoScopeDestination() {
      if (typeof window === 'undefined') {
        return null;
      }

      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('scope', SETTINGS_ALL_SHOPS_QUERY_VALUE);
      currentUrl.searchParams.delete('selected_shop');

      return currentUrl.toString();
    },

    visitScopeDestination(destination) {
      if (!destination) {
        return;
      }

      router.visit(destination, {
        preserveState: false,
        showProgress: false,
      });
    },

    syncTheme(theme = null) {
      this.currentTheme =
        theme || document.documentElement.getAttribute('data-bs-theme') || 'light';
    },

    handleThemeChange(event) {
      this.syncTheme(event?.detail?.theme || null);
    },

    handleScopeChange(scope) {
      if (!scope) {
        return;
      }

      this.$emit('scope-change', scope);

      if (scope?.shopId) {
        const destination = this.buildShopScopeDestination(scope.shopId);

        if (destination) {
          this.visitScopeDestination(destination);
          return;
        }

        this.visitScopeDestination(
          route('dashboard.select-shop', {
            shop_id: scope.shopId,
            user_id: this.$page.props.auth.user.id,
            query: window.location.search,
          }),
        );
        return;
      }

      this.visitScopeDestination(this.buildMsoScopeDestination());
    },

    handleScopePickerChange(scope) {
      this.$refs.scopePicker?.hide();
      this.$refs.scopeTrigger?.$el?.focus();
      this.handleScopeChange(scope);
    },

    handleSearchTrigger(event) {
      event?.preventDefault?.();
      this.$emit('open-command-palette');
    },

    navigateTo(href, useInertia = false) {
      if (!href || href === '#') {
        return;
      }

      if (useInertia) {
        router.visit(href);
        return;
      }

      window.location = href;
    },

    scopeDisplayName(scope) {
      if (!scope) {
        return 'Select shop';
      }

      return scope.shortName || scope.name || 'Select shop';
    },

    scopeMeta(scope) {
      if (!scope) {
        return '';
      }

      if (scope.isAllShops) {
        return 'Manage details across locations';
      }

      return [scope.city, scope.state].filter(Boolean).join(', ');
    },

    toggleUserMenu(event) {
      this.$refs.userMenu.toggle(event);
    },

    openUserMenu(event) {
      this.$refs.userMenu?.show(event);
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.focusFirstPopupItem('userMenu');
        });
      });
    },

    focusFirstPopupItem(refName) {
      const popup = this.$refs[refName]?.$el;
      const firstItem = popup?.querySelector('[role="menuitem"]');
      firstItem?.focus?.();
    },

    handleUserTriggerKeydown(event) {
      if (event.key !== 'ArrowDown') {
        return;
      }

      event.preventDefault();
      this.openUserMenu(event);
    },

    openProfileTimeClock() {
      this.$nextTick(() => {
        this.$refs.profileTimeClock?.open();
      });
    },
  },
};
</script>

<template>
  <Toolbar
    :aria-label="`${productName} actions`"
    :class="topbarClass"
  >
    <template #start>
      <div class="shop-management-topbar-start">
        <SidebarTrigger
          v-if="showSidebarTrigger"
          :target="sidebarTarget"
          asChild
          v-slot="{ class: triggerClass, a11yAttrs, onClick }"
        >
          <Button
            type="button"
            :class="triggerClass"
            variant="text"
            severity="secondary"
            iconOnly
            aria-label="Toggle navigation"
            v-bind="a11yAttrs"
            @click="onClick"
          >
            <i class="fa-solid fa-bars" />
          </Button>
        </SidebarTrigger>

        <Button
          asChild
          variant="text"
          :iconOnly="isIconFirstMode"
          v-slot="{ class: buttonClass, a11yAttrs }"
        >
          <Link
            :href="brandHref"
            :class="[buttonClass, 'shop-management-topbar-brand']"
            :aria-label="`${productName} dashboard`"
            v-bind="a11yAttrs"
          >
            <img
              v-if="logoUrl"
              :src="logoUrl"
              alt=""
              aria-hidden="true"
              :width="logoWidth"
              :height="logoHeight"
              :class="[
                'shop-management-topbar-logo',
                showBrandLabel
                  ? 'shop-management-topbar-logo--lockup'
                  : 'shop-management-topbar-logo--icon',
              ]"
            />
            <span v-else>{{ showBrandLabel ? productName : productInitials }}</span>
          </Link>
        </Button>

        <div v-if="!isAdmin" class="shop-management-topbar-search">
          <Button
            type="button"
            variant="outlined"
            severity="secondary"
            :iconOnly="isSearchIconOnly"
            class="shop-management-topbar-search-trigger"
            aria-label="Open command palette"
            aria-haspopup="dialog"
            :aria-controls="commandPaletteId"
            :aria-expanded="String(commandPaletteOpen)"
            :aria-keyshortcuts="shortcutsEnabled ? searchShortcutAria : undefined"
            @click="handleSearchTrigger"
          >
            <i class="fa-solid fa-magnifying-glass" aria-hidden="true" />
            <span v-if="!isSearchIconOnly" class="shop-management-topbar-search-label">Search</span>
            <kbd v-if="shortcutsEnabled && !isSearchIconOnly" class="shop-management-topbar-search-shortcut">
              {{ searchShortcutLabel }}
            </kbd>
          </Button>
        </div>
      </div>
    </template>

    <template #center>
      <div
        class="shop-management-topbar-center-actions"
        :class="{ 'shop-management-topbar-admin-context': isAdmin }"
      >
        <template v-if="isAdmin">
          <ShopSearch class="shop-management-topbar-admin-search" />
          <Button
            v-if="adminWorkspace.context.value?.returnMsoId"
            label="Back to MSO"
            icon="fa-solid fa-arrow-left"
            severity="secondary"
            variant="text"
            size="small"
            :disabled="adminWorkspace.state.navigationBusy"
            @click="adminWorkspace.returnToMso"
          />
        </template>
        <TimeClockControl
          v-if="showTimeClock"
          :user="user"
          :icon-only="isIconFirstMode"
          :size="null"
        />
        <Button
          v-if="showEstimate"
          type="button"
          severity="primary"
          :iconOnly="isIconFirstMode"
          class="shop-management-topbar-action"
          @click="$emit('create-estimate')"
        >
          <i class="fa-solid fa-plus" aria-hidden="true" />
          <span v-if="!isIconFirstMode">{{ estimateLabel }}</span>
        </Button>
      </div>
    </template>

    <template #end>
      <div class="shop-management-topbar-end">
        <Button
          v-if="isScopeIconOnlyMode"
          ref="scopeTrigger"
          type="button"
          icon="fa-solid fa-store"
          variant="outlined"
          severity="secondary"
          iconOnly
          class="shop-management-topbar-icon-button"
          :aria-label="scopeTriggerLabel"
          aria-haspopup="dialog"
          :aria-controls="scopePickerId"
          :aria-expanded="String(scopePickerOpen)"
          @click="$refs.scopePicker.toggle($event)"
          @keydown.down.prevent="$refs.scopePicker.show($event)"
        />
        <Select
          v-if="!isAdmin && !isScopeIconOnlyMode"
          :model-value="activeScope"
          :options="scopes"
          optionLabel="name"
          dataKey="id"
          appendTo="body"
          filter
          :filterFields="['name', 'shortName', 'city', 'state']"
          filterPlaceholder="Search shops"
          filterIcon="fa-solid fa-magnifying-glass"
          dropdownIcon="fa-solid fa-chevron-down"
          :auto-filter-focus="!touchPickerDevice"
          :focus-on-hover="false"
          :pt="{ pcFilter: { root: { 'aria-label': 'Search shops' } } }"
          placeholder="Select shop"
          aria-label="Select shop"
          class="shop-management-topbar-scope"
          scrollHeight="var(--p-bayhive-layout-topbar-scope-options-height)"
          @update:modelValue="handleScopeChange"
        >
          <template #value="slotProps">
            <div v-if="slotProps.value" class="shop-management-topbar-scope-value">
              <i class="fa-solid fa-store" aria-hidden="true" />
              <span class="shop-management-topbar-scope-copy">
                <span class="shop-management-topbar-scope-name">{{ scopeDisplayName(slotProps.value) }}</span>
              </span>
            </div>
            <span v-else class="shop-management-topbar-scope-value shop-management-topbar-scope-value--placeholder">
              <i class="fa-solid fa-store" aria-hidden="true" />
              <span class="shop-management-topbar-scope-name">{{ slotProps.placeholder }}</span>
            </span>
          </template>

          <template #option="slotProps">
            <div class="shop-management-topbar-scope-option">
              <i class="fa-solid fa-store" aria-hidden="true" />
              <span class="shop-management-topbar-scope-copy">
                <span class="shop-management-topbar-scope-name">{{ slotProps.option.name }}</span>
                <small v-if="scopeMeta(slotProps.option)" class="shop-management-topbar-scope-meta">
                  {{ scopeMeta(slotProps.option) }}
                </small>
              </span>
            </div>
          </template>
        </Select>

        <NotificationBell v-if="!isAdmin" />

        <Button
          v-if="isUserIconOnlyMode"
          type="button"
          variant="outlined"
          severity="secondary"
          :aria-label="displayName"
          class="shop-management-topbar-user-trigger shop-management-topbar-user-trigger--icon"
          iconOnly
          aria-haspopup="menu"
          :aria-controls="userMenuId"
          :aria-expanded="String(userMenuOpen)"
          @click="toggleUserMenu"
          @keydown.down="handleUserTriggerKeydown"
        >
          <Avatar
            :image="userAvatarImage"
            :label="userAvatarImage ? null : initials"
            shape="circle"
            class="shop-management-topbar-user-avatar"
            imageClass="shop-management-topbar-user-avatar-image"
            :aria-label="displayName"
          />
        </Button>

        <Button
          v-else
          type="button"
          variant="outlined"
          severity="secondary"
          :aria-label="displayName"
          class="shop-management-topbar-user-trigger"
          aria-haspopup="menu"
          :aria-controls="userMenuId"
          :aria-expanded="String(userMenuOpen)"
          @click="toggleUserMenu"
          @keydown.down="handleUserTriggerKeydown"
        >
          <Avatar
            :image="userAvatarImage"
            :label="userAvatarImage ? null : initials"
            shape="circle"
            class="shop-management-topbar-user-avatar"
            imageClass="shop-management-topbar-user-avatar-image"
            :aria-label="displayName"
          />
          <span v-if="showUserLabel" class="shop-management-topbar-user-label">{{ displayName }}</span>
          <i v-if="showUserChevron" class="fa-solid fa-chevron-down shop-management-topbar-user-chevron" />
        </Button>
      </div>
    </template>
  </Toolbar>

  <Popover
    v-if="isScopeIconOnlyMode"
    ref="scopePicker"
    :id="scopePickerId"
    aria-label="Select shop"
    class="shop-management-topbar-scope-picker"
    @show="scopePickerOpen = true"
    @hide="scopePickerOpen = false"
  >
    <Listbox
      :model-value="activeScope"
      :options="scopes"
      optionLabel="name"
      dataKey="id"
      filter
      :filterFields="['name', 'shortName', 'city', 'state']"
      filterPlaceholder="Search shops"
      filterIcon="fa-solid fa-magnifying-glass"
      :focusOnHover="false"
      aria-label="Shops"
      emptyMessage="No shops available."
      emptyFilterMessage="No shops match your search."
      scrollHeight="var(--p-bayhive-layout-topbar-scope-options-height)"
      :pt="{ pcFilter: { root: { autofocus: !touchPickerDevice, 'aria-label': 'Search shops' } } }"
      @update:modelValue="handleScopePickerChange"
    >
      <template #option="{ option }">
        <div class="shop-management-topbar-scope-option">
          <i class="fa-solid fa-store" aria-hidden="true" />
          <span class="shop-management-topbar-scope-copy">
            <span class="shop-management-topbar-scope-name">{{ option.name }}</span>
            <small v-if="scopeMeta(option)" class="shop-management-topbar-scope-meta">
              {{ scopeMeta(option) }}
            </small>
          </span>
        </div>
      </template>
    </Listbox>
  </Popover>

  <Menu
    ref="userMenu"
    :id="userMenuId"
    :model="userMenuItems"
    popup
    appendTo="body"
    @show="userMenuOpen = true"
    @hide="userMenuOpen = false"
  >
    <template #item="slotProps">
      <a v-bind="slotProps.props.action" class="shop-management-topbar-menu-item shop-management-topbar-menu-item--user">
        <i :class="slotProps.item.icon" aria-hidden="true" />
        <span>{{ slotProps.item.label }}</span>
      </a>
    </template>
  </Menu>

  <TimeClockControl
    v-if="showTimeClock"
    ref="profileTimeClock"
    :user="user"
    presentation="dialog"
    :show-trigger="false"
  />
</template>

<style scoped>
.shop-management-topbar {
  --p-bayhive-topbar-control-size: max(var(--p-button-icon-only-width), var(--p-bayhive-touch-min-size, 0px));
  border-radius: 0;
  border-width: 0 0 1px;
  box-sizing: border-box;
  min-height: var(--p-bayhive-layout-topbar-height);
  max-width: 100%;
  width: 100%;
  overflow: visible;
  flex-wrap: nowrap;
  margin-bottom: 0;
  position: static;
}

/* Button has no block-size token. Keep header buttons aligned with fields,
   including the preset's minimum target size on touch-first devices. */
.shop-management-topbar :deep(.p-button),
.shop-management-topbar :deep(#alertsDropdown) {
  block-size: var(--p-bayhive-topbar-control-size);
  min-block-size: var(--p-bayhive-topbar-control-size);
}

.shop-management-topbar :deep(#alertsDropdown) {
  align-items: center;
  color: var(--p-text-color);
  display: inline-flex;
  inline-size: var(--p-bayhive-topbar-control-size);
  justify-content: center;
  min-inline-size: var(--p-bayhive-topbar-control-size);
  padding: 0;
  text-decoration: none;
}

.shop-management-topbar :deep(#alertsDropdown:focus-visible) {
  box-shadow: var(--p-form-field-focus-ring-shadow);
}

.shop-management-topbar :deep(#alertsDropdown .indicator) {
  border-radius: 50%;
  box-shadow: 0 .1rem .2rem rgba(0, 0, 0, .05);
  color: #fff;
  display: block;
  font-size: .675rem;
  height: 18px;
  padding: 1px;
  position: absolute;
  right: -11px;
  text-align: center;
  top: -6px;
  transition: top .1s ease-out;
  width: 18px;
}

.shop-management-topbar :deep(.p-toolbar-start) {
  min-width: var(--p-bayhive-layout-topbar-start-width);
  flex: 1 0 var(--p-bayhive-layout-topbar-start-width);
}

.shop-management-topbar :deep(.p-toolbar-center) {
  flex: 0 0 auto;
}

.shop-management-topbar :deep(.p-toolbar-end) {
  flex: 0 1 auto;
  min-width: 0;
}

.shop-management-topbar--compact :deep(.p-toolbar-start) {
  min-width: var(--p-bayhive-layout-topbar-compact-start-width);
  flex-basis: var(--p-bayhive-layout-topbar-compact-start-width);
}

.shop-management-topbar--icon-first :deep(.p-toolbar-start) {
  min-width: 0;
  flex: 1 0 auto;
}

.shop-management-topbar--icon-first :deep(.p-toolbar-end) {
  flex: 0 0 auto;
}

.shop-management-topbar-start {
  display: flex;
  align-items: center;
  gap: var(--p-bayhive-spacing-sm);
  width: 100%;
  min-width: 0;
}

/* Admin uses the middle of the existing shell header for account context. */
.shop-management-topbar--admin {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  column-gap: var(--p-bayhive-spacing-lg);
}

.shop-management-topbar--admin :deep(.p-toolbar-start),
.shop-management-topbar--admin :deep(.p-toolbar-center) {
  min-width: 0;
}

.shop-management-topbar--admin :deep(.p-toolbar-end) {
  justify-self: end;
}

.shop-management-topbar-admin-context {
  width: 100%;
  min-width: 0;
}

.shop-management-topbar-admin-search {
  flex: 1 1 24rem;
  min-width: 0;
}

.shop-management-topbar--admin.shop-management-topbar--icon-first {
  grid-template-columns: minmax(0, 1fr) auto;
  row-gap: var(--p-bayhive-spacing-sm);
}

.shop-management-topbar--admin.shop-management-topbar--icon-first :deep(.p-toolbar-start) {
  grid-column: 1;
  grid-row: 1;
}

.shop-management-topbar--admin.shop-management-topbar--icon-first :deep(.p-toolbar-end) {
  grid-column: 2;
  grid-row: 1;
}

.shop-management-topbar--admin.shop-management-topbar--icon-first :deep(.p-toolbar-center) {
  grid-column: 1 / -1;
  grid-row: 2;
}

.shop-management-topbar--admin.shop-management-topbar--narrow-layout .shop-management-topbar-admin-context {
  flex-direction: column;
  align-items: stretch;
  gap: var(--p-bayhive-spacing-sm);
}

.shop-management-topbar--admin.shop-management-topbar--narrow-layout .shop-management-topbar-admin-search {
  flex-basis: auto;
}

.shop-management-topbar-brand {
  min-width: 0;
  flex-shrink: 0;
  text-decoration: none;
}

.shop-management-topbar-brand:hover,
.shop-management-topbar-brand:focus,
.shop-management-topbar-brand:focus-visible {
  text-decoration: none;
}

.shop-management-topbar-logo {
  display: block;
  height: 2rem;
  object-fit: contain;
}

.shop-management-topbar-logo--lockup {
  width: auto;
  max-width: 9.5rem;
  object-position: left center;
}

.shop-management-topbar--icon-first .shop-management-topbar-logo {
  margin-inline-end: 0;
}

.shop-management-topbar--icon-first .shop-management-topbar-brand {
  align-items: center;
  block-size: var(--p-bayhive-topbar-control-size);
  display: inline-flex;
  inline-size: var(--p-bayhive-topbar-control-size);
  justify-content: center;
  min-block-size: var(--p-bayhive-topbar-control-size);
  min-inline-size: var(--p-bayhive-topbar-control-size);
  padding: 0;
}

.shop-management-topbar-logo--icon {
  width: 1.625rem;
}

.shop-management-topbar-search {
  flex: 1 1 var(--p-bayhive-layout-topbar-search-width);
  min-width: var(--p-bayhive-layout-topbar-search-min-width);
}

.shop-management-topbar-search-trigger {
  justify-content: flex-start;
  width: 100%;
  border-color: var(--p-form-field-border-color);
  background: var(--p-form-field-background);
  color: var(--p-form-field-placeholder-color);
  font-weight: 400;
}

.shop-management-topbar-search-trigger:hover {
  border-color: var(--p-form-field-hover-border-color);
  background: var(--p-form-field-background);
  color: var(--p-form-field-color);
}

.shop-management-topbar-search-trigger:focus-visible {
  border-color: var(--p-form-field-focus-border-color);
  background: var(--p-form-field-background);
}

.shop-management-topbar--icon-first .shop-management-topbar-search-trigger {
  justify-content: center;
  width: var(--p-bayhive-topbar-control-size);
  min-width: var(--p-bayhive-topbar-control-size);
}

.shop-management-topbar-search-label {
  min-width: 0;
}

.shop-management-topbar-search-shortcut {
  display: inline-flex;
  align-self: stretch;
  align-items: center;
  justify-content: center;
  margin-inline-start: auto;
  border: 0;
  background: transparent;
  color: var(--p-text-muted-color);
  font: inherit;
  font-size: 0.875em;
  line-height: 1;
  white-space: nowrap;
}

.shop-management-topbar--compact .shop-management-topbar-search {
  flex-basis: var(--p-bayhive-layout-topbar-compact-search-width);
  min-width: var(--p-bayhive-layout-topbar-compact-search-width);
}

.shop-management-topbar--icon-first .shop-management-topbar-search {
  flex: 0 0 var(--p-bayhive-topbar-control-size);
  min-width: 0;
}

.shop-management-topbar-action {
  white-space: nowrap;
}

.shop-management-topbar-center-actions {
  align-items: center;
  display: flex;
  gap: var(--p-bayhive-spacing-sm);
}

.shop-management-topbar-end {
  display: flex;
  align-items: center;
  gap: var(--p-bayhive-spacing-sm);
  min-width: 0;
}

.shop-management-topbar--compact .shop-management-topbar-end {
  gap: var(--p-bayhive-spacing-xs);
}

.shop-management-topbar--icon-first .shop-management-topbar-start,
.shop-management-topbar--icon-first .shop-management-topbar-center-actions,
.shop-management-topbar--icon-first .shop-management-topbar-end {
  gap: var(--p-bayhive-spacing-xs);
}

/* Reserve room for the touch targets before adding gaps between controls. */
@container bayhive-topbar (max-width: 28rem) {
  .shop-management-topbar--icon-first:not(.shop-management-topbar--admin) {
    --topbar-gap: clamp(
      0px,
      calc(
        (100cqi - 8 * var(--p-bayhive-topbar-control-size) - 2 * var(--p-bayhive-spacing-xs)) / 7
      ),
      var(--p-bayhive-spacing-xs)
    );
    gap: var(--topbar-gap);
    padding-inline: var(--p-bayhive-spacing-xs);
  }

  .shop-management-topbar--icon-first:not(.shop-management-topbar--admin)
    :is(
      .shop-management-topbar-start,
      .shop-management-topbar-center-actions,
      .shop-management-topbar-end
    ) {
    gap: var(--topbar-gap);
  }
}

/* Below eight full touch targets, the dashboard remains available in the
   navigation menu; keep every operational control on the same row. */
@media (pointer: coarse) {
  @container bayhive-topbar (width < 22.5rem) {
    .shop-management-topbar--icon-first.shop-management-topbar--all-controls:not(.shop-management-topbar--admin)
      .shop-management-topbar-brand {
      display: none;
    }
  }
}

.shop-management-topbar-scope {
  /* Match the header buttons; search lives in the Select dropdown. */
  block-size: var(--p-bayhive-topbar-control-size);
  flex: 1 1 auto;
  min-block-size: var(--p-bayhive-topbar-control-size);
  width: max-content;
  min-width: var(--p-bayhive-layout-topbar-scope-min-width);
  max-width: none;
}

.shop-management-topbar--compact .shop-management-topbar-scope {
  flex-basis: var(--p-bayhive-layout-topbar-compact-scope-width);
  max-width: var(--p-bayhive-layout-topbar-compact-scope-width);
}

.shop-management-topbar-scope :deep(.p-select-label) {
  align-self: center;
  min-width: 0;
}

.shop-management-topbar-scope-picker {
  width: min(22rem, calc(100vw - 2rem));
}

.shop-management-topbar-scope-value,
.shop-management-topbar-scope-option {
  display: flex;
  align-items: center;
  gap: var(--p-bayhive-spacing-md);
  min-width: 0;
}

.shop-management-topbar-scope-copy {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.shop-management-topbar-scope-name,
.shop-management-topbar-scope-meta {
  min-width: 0;
  overflow: hidden;
  text-overflow: clip;
  white-space: nowrap;
}

.shop-management-topbar-scope-meta {
  opacity: 0.72;
}

.shop-management-topbar-scope-option .shop-management-topbar-scope-name,
.shop-management-topbar-scope-option .shop-management-topbar-scope-meta {
  text-overflow: ellipsis;
}

.shop-management-topbar-scope-value--placeholder {
  opacity: 0.75;
}

.shop-management-topbar-icon-button,
.shop-management-topbar-action {
  flex-shrink: 0;
}

.shop-management-topbar-user-trigger {
  flex: 1 1 auto;
  width: max-content;
  min-width: 0;
  max-width: none;
}

.shop-management-topbar--compact .shop-management-topbar-user-trigger {
  flex-basis: auto;
  width: auto;
  min-width: var(--p-bayhive-touch-min-size, 0px);
  max-width: none;
}

.shop-management-topbar-user-trigger--icon {
  flex: 0 0 auto;
  width: auto;
  max-width: none;
}

.shop-management-topbar :deep(.shop-management-topbar-user-avatar.p-avatar) {
  overflow: hidden;
  flex-shrink: 0;
}

.shop-management-topbar :deep(.shop-management-topbar-user-avatar-image) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.shop-management-topbar-user-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: clip;
  white-space: nowrap;
}

.shop-management-topbar-user-chevron {
  margin-inline-start: var(--p-bayhive-spacing-sm);
}

.shop-management-topbar-menu-item {
  width: 100%;
  text-decoration: none;
  color: inherit;
}

.shop-management-topbar-menu-item--user {
  display: flex;
  align-items: center;
  gap: var(--p-bayhive-spacing-sm);
}

</style>
